import { Controller } from 'stimulus'

// The Swift iOS app (PR #37668) lists its native features in the user agent:
// `SimpleroNative/1 (media; share)`. The server reads the same token with this
// pattern (SimpleroNativeApp::PATTERN on that PR); keep the two in sync. The app
// handles the `SHARE` bridge message ({ url, title? }) once ENG-36851 lands.
const NATIVE_UA = /SimpleroNative\/\d+(?: \(([^)]*)\))?/
const SWALLOWED_ERRORS = ['AbortError', 'InvalidStateError']

// Makes its element a share trigger. Inside the native app the bridge path
// needs the app to advertise the `share` capability (ENG-36851 / PR #37668);
// the React Native app never sends `SimpleroNative/`. Elsewhere we fall back
// to the Web Share API. With neither, the element is removed from the page.
export default class extends Controller {
  // tabbable: the menu item should join keyboard navigation once sharing works
  static values = { url: String, title: String, tabbable: Boolean }

  get capabilities() {
    const match = (navigator.userAgent || '').match(NATIVE_UA)
    if (!match || !match[1]) return []
    return match[1]
      .split(';')
      .map((c) => c.trim())
      .filter(Boolean)
  }

  get mode() {
    if (this.capabilities.includes('share') && window.Bridge?.share) {
      return 'bridge'
    }
    if (
      typeof navigator.share === 'function' &&
      // Chromium tells us when a parent frame blocks sharing
      globalThis.document?.featurePolicy?.allowsFeature?.('web-share') !==
        false &&
      navigator.canShare?.({ url: this.urlValue }) !== false
    ) {
      return 'web'
    }
    return null
  }

  connect() {
    if (!this.mode) {
      this.element.remove()
      return
    }
    this.element.hidden = false
    // Menu rows are rendered without data-tabbable-item so a hidden row can't
    // be reached by keyboard before this controller has loaded.
    if (this.tabbableValue) this.element.dataset.tabbableItem = '1'
  }

  share(event) {
    event.preventDefault()
    const data = { url: this.urlValue }
    if (this.titleValue && this.titleValue.trim()) data.title = this.titleValue

    if (this.mode === 'bridge') {
      window.Bridge.share(data.url, data.title)
      return
    }
    // No await before navigator.share: Safari needs the tap's user activation.
    // AbortError: the member closed the sheet. InvalidStateError: a sheet is
    // already open (double tap). Anything else (e.g. NotAllowedError after a
    // lost tap) is a real bug, so it still surfaces.
    return navigator.share(data).catch((e) => {
      if (!SWALLOWED_ERRORS.includes(e?.name)) throw e
    })
  }
}
